import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowUpIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import { useI18n } from '../i18n/I18nProvider';
import ReorderPuzzle from './ReorderPuzzle';

const MAX_ASSISTANT_TURNS = 6;

const createHintThreadId = () => (
  `hint_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
);

const ReorderingQuestion = ({
  question,
  scrambled,
  cnHintText,
  cnShown,
  onToggleCn,
  redoNoHintActive,
  onSubmit,
  inputsDisabled,
  captureMode,
  renderCaptureToken,
  tier,
  sessionId,
  onAiHintUsed,
}) => {
  const { locale, t } = useI18n();
  const threadIdRef = useRef(createHintThreadId());
  const requestInFlightRef = useRef(false);
  const inputRef = useRef(null);
  const [draft, setDraft] = useState({ answer: '', unusedTokens: [] });
  const [coachOpen, setCoachOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [followUp, setFollowUp] = useState('');
  const [remainingFollowUps, setRemainingFollowUps] = useState(5);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const assistantTurns = useMemo(
    () => messages.filter((message) => message.role === 'assistant').length,
    [messages],
  );

  useEffect(() => {
    if (redoNoHintActive) {
      setCoachOpen(false);
      setFollowUp('');
    }
  }, [redoNoHintActive]);

  const handlePuzzleProgress = useCallback((nextDraft) => {
    setDraft({
      answer: String(nextDraft?.answer || ''),
      unusedTokens: Array.isArray(nextDraft?.unusedTokens)
        ? nextDraft.unusedTokens.map((token) => String(token || '')).filter(Boolean)
        : [],
    });
  }, []);

  const requestHint = async (studentQuestion = '') => {
    const cleanQuestion = String(studentQuestion || '').trim();
    const turn = assistantTurns + 1;
    if (loading || requestInFlightRef.current || turn > MAX_ASSISTANT_TURNS) return;

    requestInFlightRef.current = true;
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/ai/reordering-hint', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'Accept-Language': locale,
        },
        body: JSON.stringify({
          session_id: sessionId,
          hint_thread_id: threadIdRef.current,
          turn,
          word: question.word,
          sense_id: question.item?.sense_id || 'primary',
          tier,
          current_answer: draft.answer,
          unused_tokens: draft.unusedTokens,
          question: cleanQuestion,
          history: messages.slice(-5).map(({ role, content }) => ({ role, content })),
          ui_language: locale,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.reply) {
        throw new Error(data.message || 'AI coach is unavailable right now.');
      }

      setMessages((current) => [
        ...current,
        ...(cleanQuestion ? [{
          id: `${threadIdRef.current}-${turn}-user`,
          role: 'user',
          content: cleanQuestion,
        }] : []),
        {
          id: `${threadIdRef.current}-${turn}-assistant`,
          role: 'assistant',
          content: data.reply,
        },
      ]);
      setRemainingFollowUps(
        Number.isInteger(data.remaining_follow_ups)
          ? Math.max(0, data.remaining_follow_ups)
          : Math.max(0, MAX_ASSISTANT_TURNS - turn),
      );
      setFollowUp('');
      onAiHintUsed?.();
      window.setTimeout(() => inputRef.current?.focus(), 0);
    } catch (requestError) {
      setError(requestError.message || 'AI coach is unavailable right now.');
    } finally {
      requestInFlightRef.current = false;
      setLoading(false);
    }
  };

  const openCoach = () => {
    setCoachOpen(true);
    if (assistantTurns === 0 && !loading) requestHint();
  };

  const submitFollowUp = (event) => {
    event.preventDefault();
    requestHint(followUp);
  };

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-lg">{t('Reorder the following sentence:')}</p>
        {!redoNoHintActive && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => onToggleCn?.(!cnShown)}
              disabled={inputsDisabled}
              className={`inline-flex min-h-10 items-center rounded-md px-3 py-2 text-sm font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                cnShown
                  ? 'bg-amber-600 hover:bg-amber-700'
                  : 'bg-amber-500 hover:bg-amber-600'
              }`}
              title={t(cnShown ? 'Hide Chinese translation' : 'Show Chinese translation')}
            >
              {t(cnShown ? 'Hide Chinese' : 'Show Chinese')}
            </button>
            <button
              type="button"
              onClick={openCoach}
              disabled={inputsDisabled}
              className="inline-flex min-h-10 items-center gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 transition-colors hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50"
              title={t('Ask the AI coach for a hint')}
            >
              <SparklesIcon className="h-4 w-4" aria-hidden="true" />
              {t('AI hint')}
            </button>
          </div>
        )}
      </div>

      {redoNoHintActive && (
        <div className="mb-3 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
          {t('Great! Now rebuild it once without hints.')}
        </div>
      )}
      {cnShown && !redoNoHintActive && (
        <div className="mb-3 rounded-md border border-yellow-200 bg-yellow-50 p-3 text-yellow-900">
          {cnHintText || t('No Chinese hint available')}
        </div>
      )}

      {coachOpen && !redoNoHintActive && (
        <section className="mb-4 overflow-hidden rounded-md border border-indigo-200 bg-indigo-50/50">
          <header className="flex items-center justify-between border-b border-indigo-100 bg-white px-4 py-3">
            <div className="flex items-center gap-2 font-semibold text-gray-900">
              <SparklesIcon className="h-5 w-5 text-indigo-600" aria-hidden="true" />
              {t('AI sentence coach')}
            </div>
            <button
              type="button"
              onClick={() => setCoachOpen(false)}
              className="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              title={t('Close')}
              aria-label={t('Close')}
            >
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </header>

          <div className="max-h-72 space-y-3 overflow-y-auto px-4 py-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={message.role === 'user' ? 'flex justify-end' : 'flex justify-start'}
              >
                <div
                  data-i18n-skip
                  className={`max-w-[88%] whitespace-pre-wrap rounded-md px-3 py-2 text-sm leading-6 ${
                    message.role === 'user'
                      ? 'bg-indigo-600 text-white'
                      : 'border border-gray-200 bg-white text-gray-800'
                  }`}
                >
                  {message.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex items-center gap-2 text-sm text-indigo-700">
                <span className="h-2 w-2 animate-pulse rounded-full bg-indigo-500" />
                <span>{t('Looking at your sentence...')}</span>
              </div>
            )}
            {error && !loading && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                <div>{t(error)}</div>
                <button
                  type="button"
                  onClick={() => requestHint(followUp)}
                  className="mt-2 font-semibold text-red-800 underline"
                >
                  {t('Try again')}
                </button>
              </div>
            )}
          </div>

          {assistantTurns > 0 && (
            <div className="border-t border-indigo-100 bg-white p-3">
              {remainingFollowUps > 0 ? (
                <form onSubmit={submitFollowUp} className="flex items-end gap-2">
                  <div className="min-w-0 flex-1">
                    <label className="sr-only" htmlFor={`hint-follow-up-${threadIdRef.current}`}>
                      {t('Ask a follow-up question')}
                    </label>
                    <textarea
                      ref={inputRef}
                      id={`hint-follow-up-${threadIdRef.current}`}
                      value={followUp}
                      onChange={(event) => setFollowUp(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' && !event.shiftKey) {
                          event.preventDefault();
                          submitFollowUp(event);
                        }
                      }}
                      rows={1}
                      maxLength={500}
                      disabled={loading}
                      placeholder={t('Ask a question, or send blank for the next hint')}
                      className="max-h-24 min-h-10 w-full resize-none rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:bg-gray-100"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                    title={t('Send question or get next hint')}
                    aria-label={t('Send question or get next hint')}
                  >
                    <ArrowUpIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </form>
              ) : (
                <p className="text-sm font-medium text-indigo-800">
                  {t('Now use the guidance and try the sentence yourself.')}
                </p>
              )}
              <p className="mt-2 text-xs text-gray-500">
                {t('{count} follow-up questions left', { count: remainingFollowUps })}
              </p>
            </div>
          )}
        </section>
      )}

      <ReorderPuzzle
        scrambled={scrambled}
        onSubmit={onSubmit}
        onProgressChange={handlePuzzleProgress}
        disabled={inputsDisabled}
        captureMode={captureMode}
        renderCaptureToken={renderCaptureToken}
      />
    </div>
  );
};

export default ReorderingQuestion;
